import React from 'react';
import { Box, TextField, MenuItem, Typography, Pagination } from '@mui/material';

/**
 * Reusable Table Pagination Component
 * 
 * @param {Object} paginationModel - Current pagination state { page: number, pageSize: number }
 * @param {Function} onPaginationModelChange - Callback function to update pagination state
 * @param {number} totalCount - Total number of items
 * @param {Array<number>} pageSizeOptions - Array of page size options (default: [5, 10, 25, 50])
 * @param {boolean} showPageInfo - Whether to show page information (default: true)
 * @param {boolean} showRangeInfo - Whether to show range information (default: true)
 * @param {boolean} showPageSizeSelector - Whether to show page size selector (default: true)
 */
const TablePagination = ({
    paginationModel,
    onPaginationModelChange,
    totalCount,
    pageSizeOptions = [5, 10, 25, 50],
    showPageInfo = true,
    showRangeInfo = true,
    showPageSizeSelector = true,
}) => {
    const totalPages = Math.ceil(totalCount / paginationModel.pageSize);

    const handlePageChange = (event, value) => {
        onPaginationModelChange((prev) => ({ ...prev, page: value - 1 }));
    };

    const handlePageSizeChange = (e) => {
        onPaginationModelChange((prev) => ({
            ...prev,
            pageSize: Number(e.target.value),
            page: 0,
        }));
    };

    const getRangeText = () => {
        if (totalCount === 0) return '0 of 0';
        const start = paginationModel.page * paginationModel.pageSize + 1;
        const end = Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalCount);
        return `${start}–${end} of ${totalCount}`;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                p: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {showPageSizeSelector && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            select
                            label="Limit"
                            value={paginationModel.pageSize}
                            onChange={handlePageSizeChange}
                            size="small"
                        >
                            {pageSizeOptions.map((size) => (
                                <MenuItem key={size} value={size}>
                                    {size}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                )}

                {showRangeInfo && (
                    <Typography variant="body2" color="text.dark">
                        {getRangeText()}
                    </Typography>
                )}

                {showPageInfo && totalCount > 0 && (
                    <Typography variant="body2" color="text.dark">
                        (Page {paginationModel.page + 1} of {totalPages})
                    </Typography>
                )}
            </Box>

            <Pagination
                count={totalPages}
                page={paginationModel.page + 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
            />
        </Box>
    );
};

export default TablePagination;

